import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import { Close, NavigateBefore, NavigateNext } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function GuideModal({ open, onClose }) {
  const location = useLocation();

  // URL 파라미터에 따른 가이드 이미지 설정
  const getGuideImages = () => {
    // 기본 가이드 이미지들 (항상 guide1, guide2, guide3, guide4, guide6 순서)
    return ["/guide1.svg", "/guide2.svg", "/guide3.svg", "/guide4.svg", "/guide6.svg"];
  };

  // 가이드 제목 설정
  const getGuideTitle = (index) => {
    const titles = [
      "관리자 이용가이드 - 신청내역",
      "관리자 이용가이드 - 신청상세",
      "관리자 이용가이드 - 예약내역",
      "관리자 이용가이드 - 예약상세",
      "관리자 이용가이드 - 단체관리",
    ];
    return titles[index] || "가이드";
  };

  // 페이지에 따른 초기 인덱스 설정
  const getInitialIndex = () => {
    // /yb/yearBookingRoundingList/dummy/[id] (더미 상세 페이지) - guide4가 디폴트 (인덱스 3)
    if (location.pathname.startsWith("/yb/yearBookingRoundingList/dummy/")) {
      return 3;
    }

    // /yb/yearBookingList/[id] (상세 페이지) - guide2가 디폴트 (인덱스 1)
    if (location.pathname.startsWith("/yb/yearBookingList/")) {
      return 1;
    }

    // /yb/yearBookingList (목록 페이지) - guide1이 디폴트 (인덱스 0)
    if (location.pathname === "/yb/yearBookingList") {
      return 0;
    }

    // /yb/yearBookingRoundingList (라운딩 목록 페이지) - guide3이 디폴트 (인덱스 2)
    if (location.pathname === "/yb/yearBookingRoundingList") {
      return 2;
    }

    // /yb/yearBookingGroups (단체관리 페이지) - guide6이 디폴트 (인덱스 4)
    if (location.pathname === "/yb/yearBookingGroups" || location.pathname.startsWith("/yb/yearBookingGroups/dummy/")) {
      return 4;
    }

    // 기본값 (인덱스 0)
    return 0;
  };

  const guideImages = getGuideImages();

  const [currentImageIndex, setCurrentImageIndex] = useState(getInitialIndex());

  // 이전 이미지로 이동
  const handlePrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? guideImages.length - 1 : prev - 1
    );
  };

  // 다음 이미지로 이동
  const handleNext = () => {
    setCurrentImageIndex((prev) =>
      prev === guideImages.length - 1 ? 0 : prev + 1
    );
  };

  // 모달이 열릴 때마다 페이지에 맞는 인덱스로 리셋
  useEffect(() => {
    if (open) {
      setCurrentImageIndex(getInitialIndex());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // URL이 변경될 때마다 가이드 이미지 인덱스 업데이트
  useEffect(() => {
    setCurrentImageIndex(getInitialIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "80%",
          height: "80%",
          maxWidth: "none",
          maxHeight: "none",
          margin: "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ fontSize: "20px", fontWeight: "bold" }}>
          {getGuideTitle(currentImageIndex)} ({currentImageIndex + 1}/{guideImages.length})
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: "24px",
          paddingTop: "24px !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          position: "relative",
        }}
      >
        {/* 이미지 */}
        <img
          src={guideImages[currentImageIndex]}
          alt={`가이드 이미지 ${currentImageIndex + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: "12px",
          }}
        />

        {/* 네비게이션 버튼들 */}
        {guideImages.length > 0 && (
          <>
            {/* 이전 버튼 */}
            <Button
              onClick={handlePrevious}
              variant="text"
              sx={{
                position: "absolute",
                left: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                minWidth: "auto",
                padding: "12px",
                borderRadius: "50%",
                backgroundColor: "transparent",
                color: "#333333",
                fontSize: "24px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <NavigateBefore fontSize="large" />
            </Button>

            {/* 다음 버튼 */}
            <Button
              onClick={handleNext}
              variant="text"
              sx={{
                position: "absolute",
                right: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                minWidth: "auto",
                padding: "12px",
                borderRadius: "50%",
                backgroundColor: "transparent",
                color: "#333333",
                fontSize: "24px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <NavigateNext fontSize="large" />
            </Button>
          </>
        )}

        {/* 페이지 인디케이터 */}
        {guideImages.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              display: "flex",
              flexDirection: "row",
              gap: "8px",
            }}
          >
            {guideImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
